import React, { useState } from "react";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import {
    Button, Divider, Grid, IconButton, InputLabel, List, ListItem, ListItemText, Typography
} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useConselhosState } from "../context/conselhos-context";
import { useConselhosController } from "../context/conselhos-controller";
import { IMembroConselhoRequest } from "../../../../model/admin-portal/request/membro-conselho-request";
import { TextInputWithCounter } from "../../../components/text-input-with-counter";

const emptyDraft: IMembroConselhoRequest = { id: 0, nomeMembro: '', textoEntidadeRepresentada: '', textoCargo: '', dtaInicioMandato: '', dtaFimMandato: null };

export const ConselhoMembrosSection = () => {
    const { dataRequest } = useConselhosState();
    const { saveMembro, removeMembro } = useConselhosController();
    const [draft, setDraft] = useState<IMembroConselhoRequest>(emptyDraft);
    const [editingIndex, setEditingIndex] = useState<number | undefined>(undefined);

    const handleEdit = (index: number) => {
        setDraft(dataRequest.membros[index]);
        setEditingIndex(index);
    };

    const handleCancel = () => {
        setDraft(emptyDraft);
        setEditingIndex(undefined);
    };

    const handleSave = () => {
        if (!draft.nomeMembro || !draft.textoEntidadeRepresentada || !draft.textoCargo || !draft.dtaInicioMandato) return;
        saveMembro(draft, editingIndex);
        handleCancel();
    };

    return (
        <Grid item lg={12} sm={12} xl={12}>
            <Typography variant="subtitle1" fontWeight="bold">Membros</Typography>
            <Divider sx={{ mb: 1 }} />

            {dataRequest.membros.length > 0 && (
                <List dense>
                    {dataRequest.membros.map((membro, index) => (
                        <ListItem
                            key={index}
                            divider
                            secondaryAction={
                                <React.Fragment>
                                    <IconButton size="small" onClick={() => handleEdit(index)}><EditIcon fontSize="small" /></IconButton>
                                    <IconButton size="small" onClick={() => removeMembro(index)}><DeleteIcon fontSize="small" /></IconButton>
                                </React.Fragment>
                            }
                        >
                            <ListItemText
                                primary={`${membro.nomeMembro} (${membro.textoCargo})`}
                                secondary={`${membro.textoEntidadeRepresentada} — mandato: ${membro.dtaInicioMandato ? new Date(membro.dtaInicioMandato).toLocaleDateString('pt-BR') : ''} a ${membro.dtaFimMandato ? new Date(membro.dtaFimMandato).toLocaleDateString('pt-BR') : 'atual'}`}
                            />
                        </ListItem>
                    ))}
                </List>
            )}

            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item lg={3} sm={6} xl={3}>
                        <InputLabel>Nome *</InputLabel>
                        <TextInputWithCounter
                            size="small"
                            maxLength={150}
                            error={false}
                            value={draft.nomeMembro}
                            onChange={(e) => setDraft({ ...draft, nomeMembro: e.target.value })}
                            fullWidth
                        />
                    </Grid>
                    <Grid item lg={3} sm={6} xl={3}>
                        <InputLabel>Entidade Representada *</InputLabel>
                        <TextInputWithCounter
                            size="small"
                            maxLength={150}
                            error={false}
                            value={draft.textoEntidadeRepresentada}
                            onChange={(e) => setDraft({ ...draft, textoEntidadeRepresentada: e.target.value })}
                            fullWidth
                        />
                    </Grid>
                    <Grid item lg={2} sm={6} xl={2}>
                        <InputLabel>Cargo *</InputLabel>
                        <TextInputWithCounter
                            size="small"
                            maxLength={100}
                            error={false}
                            value={draft.textoCargo}
                            onChange={(e) => setDraft({ ...draft, textoCargo: e.target.value })}
                            fullWidth
                        />
                    </Grid>
                    <Grid item lg={2} sm={3} xl={2}>
                        <InputLabel>Início Mandato *</InputLabel>
                        <DesktopDatePicker
                            format='DD/MM/YYYY'
                            slotProps={{ textField: { fullWidth: true, size: "small" } }}
                            value={draft.dtaInicioMandato ? dayjs(draft.dtaInicioMandato) : null}
                            onChange={(value: Dayjs | null) => setDraft({ ...draft, dtaInicioMandato: value?.toISOString() || '' })}
                        />
                    </Grid>
                    <Grid item lg={2} sm={3} xl={2}>
                        <InputLabel>Fim Mandato</InputLabel>
                        <DesktopDatePicker
                            format='DD/MM/YYYY'
                            slotProps={{ textField: { fullWidth: true, size: "small" } }}
                            value={draft.dtaFimMandato ? dayjs(draft.dtaFimMandato) : null}
                            onChange={(value: Dayjs | null) => setDraft({ ...draft, dtaFimMandato: value?.toISOString() || null })}
                        />
                    </Grid>
                    <Grid item lg={12} sm={12} xl={12} sx={{ display: "flex", gap: 1 }}>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={editingIndex === undefined ? <AddIcon /> : undefined}
                            onClick={handleSave}
                            disabled={!draft.nomeMembro || !draft.textoEntidadeRepresentada || !draft.textoCargo || !draft.dtaInicioMandato}
                        >
                            {editingIndex === undefined ? 'Adicionar Membro' : 'Salvar Membro'}
                        </Button>
                        {editingIndex !== undefined && (
                            <Button size="small" onClick={handleCancel}>Cancelar</Button>
                        )}
                    </Grid>
                </Grid>
            </LocalizationProvider>
        </Grid>
    );
};
